import React from 'react';
import { Marker } from 'react-leaflet';
import type { Point, CategorieMarqueur } from '../../types/map';
import { createCustomIcon } from './markerUtils';
import { MarkerPopup } from '../markerInfo/MarkerPopup';
import { isExternalMode } from '../markerInfo/MarkerInfoDisplay';

interface MapMarkersProps {
  points: Point[];
  categories?: CategorieMarqueur[];
  /** Callback pour les modes modale/panneau (rendu hors MapContainer) */
  onMarkerClick?: (point: Point, categorie?: CategorieMarqueur) => void;
  /** Projet actuellement sélectionné/mis en avant (§5.4) : ses marqueurs sont agrandis. */
  selectedProjetId?: number | null;
}

export const MapMarkers: React.FC<MapMarkersProps> = ({ points, categories = [], onMarkerClick, selectedProjetId }) => {
  // Index rapide des catégories par id
  const catMap = React.useMemo(() => {
    const m = new Map<number, CategorieMarqueur>();
    categories.forEach(c => { if (c.id) m.set(c.id, c); });
    return m;
  }, [categories]);

  return (
    <>
      {points.map((point: Point) => {
        if (!point || typeof point.latitude !== 'number' || typeof point.longitude !== 'number') {
          return null;
        }

        // Résolution de la catégorie via categorieMarqueur embarqué ou lookup par id
        const categorie: CategorieMarqueur | undefined =
          point.categorieMarqueur || (point.categorieId ? catMap.get(point.categorieId) : undefined);

        const customIcon = createCustomIcon(
          point.styleIcone,
          categorie,
          selectedProjetId != null && point.projetId === selectedProjetId
        );

        // Mode d'affichage externe (modale/panneau) → clic remonte au parent
        const isExternal = isExternalMode(categorie);

        const eventHandlers = isExternal && onMarkerClick
          ? { click: () => onMarkerClick(point, categorie) }
          : {};

        return (
          <Marker 
            key={point.id}
            position={[point.latitude, point.longitude]}
            icon={customIcon}
            eventHandlers={eventHandlers}
          >
            {/* Popup uniquement en mode popup (ou fallback sans catégorie) */}
            {!isExternal && (
              <MarkerPopup point={point} categorie={categorie} />
            )}
          </Marker>
        );
      })}
    </>
  );
};
